import React from "react";
import { HashRouter, Route, Switch } from "react-router-dom";
import { ConfigProvider } from "antd";
import { Provider } from 'react-redux';
import configureStore from '@/store/configureStore'

import { i18nSetLang } from "@didi/i18n-plugin";
import { IntlProvider } from "react-intl";
import QIANKUN_CONFIG from "@/utils/qiankunConfig";
import { COMMON_PATH } from "@/utils/const";
import messages from "@/locales";
import { registerMicroApps, start } from "qiankun";
import { LANG, $t } from "@/utils"
import { DirectionType } from "antd/lib/config-provider";
import zhCN from "antd/lib/locale/zh_CN";
import ptBR from "antd/lib/locale/pt_BR";
import esES from "antd/lib/locale/es_ES";
import jaJP from "antd/lib/locale/ja_JP";
import ruRU from "antd/lib/locale/ru_RU";
import enUS from "antd/lib/locale/en_US";
import arEG from "antd/lib/locale/ar_EG";
const TicketOrder = React.lazy(() => import("@/containers/order/index"));
const TicketOrderListDetail = React.lazy(() => import("@/containers/order/detailIndex"));
const TicketDriverDetail = React.lazy(() => import("@/containers/driver/detailIndex"));
const Ticket = React.lazy(() => import("@/containers/ticket/index"));
const WorkBench = React.lazy(() => import("@/containers/ticket/workBench"));
const TicketLists = React.lazy(() => import("@/containers/ticket/ticketLists"));
const EditorPage = React.lazy(() => import("@/containers/ticket/components/ticketEditor"));
const PageErr = React.lazy(() => import("@/containers/404/"));
const DriverPage = React.lazy(() => import("@/containers/driver/index"));
const FleetPage = React.lazy(() => import("@/containers/driver/fleetIndex"));
const DriverList = React.lazy(() => import("@/containers/driver/driverList/index"));
const OrderList = React.lazy(() => import("@/containers/order/indexSearch"));
const PassengerList = React.lazy(() => import("@/containers/passenger/passengerList/index"));
const PassengerPage = React.lazy(() => import("@/containers/passenger"));
const TicketPassengerDetail = React.lazy(() => import("@/containers/passenger/detailIndex"));
const QuestionList = React.lazy(() => import("@/containers/question/index"));
const QuestionCreate = React.lazy(() => import("@/containers/question/create"));
const QuestionDetail = React.lazy(() => import("@/containers/question/detail"));
const TicketDetail = React.lazy(() => import("@/containers/ticket/detail"));
const store = configureStore()

let urlArgsLang = LANG.replace(/_/g, "-")
let lang = urlArgsLang || "en-US"
let langObj = {
    'zh-CN': {
        locale: 'zh',
        antdLocale: zhCN
    },
    'pt-BR': {
        locale: 'br',
        antdLocale: ptBR
    },
    'es-MX': {
        locale: 'mx',
        antdLocale: esES
    },
    'ja-JP': {
        locale: 'jp',
        antdLocale: jaJP
    },
    'ru-RU': {
        locale: 'ru',
        antdLocale: ruRU
    },
    'ar-EG': {
        locale: 'eg',
        antdLocale: arEG
    },
    'en-US': {
        locale: 'en',
        antdLocale: enUS
    },
}

let locale = langObj[lang].locale;
let antdLocale = langObj[lang].antdLocale;

// antd 组件direction 设置
let direction: DirectionType = "ltr"
if (LANG === 'ar_EG') {
    direction = "rtl"
}

i18nSetLang(lang || 'en-US');

// 自定义全局组件的 Empty 样式
const customizeRenderEmpty = () => (
    <div className='customize-render-empty'>
        <div className='no-data-icon'></div>
        <p>{$t("CS_info2_Data_Not_mVCx")}</p>
    </div>
);

let App = () => (
    <IntlProvider
        key={locale}
        locale={locale}
        messages={messages[locale]}
    >
        <Provider store={store}>
            <ConfigProvider locale={antdLocale} direction={direction} renderEmpty={customizeRenderEmpty}>
                <HashRouter>
                    <React.Suspense fallback={<div className="page-loading">
                        <div className="bounce bounce1"></div>
                        <div className="bounce bounce2"></div>
                    </div>}>
                        <Switch key={"Switch"}>
                            {/* 订单页 */}
                            <Route path="/ticket/order" component={TicketOrder} />
                            {/* 订单详情页 */}
                            <Route path="/ticket/order-detail" component={TicketOrderListDetail} />
                            {/* 工单页 */}
                            <Route path="/ticket" component={Ticket} exact />
                            {/* 我的工作台 */}
                            <Route path="/ark/work-bench-list" >
                                <WorkBench>
                                    <div id="ark-wrap"></div>
                                </WorkBench>
                            </Route>
                            {/* 工单列表页 */}
                            <Route path="/ark/ticket-lists" >
                                <TicketLists>
                                    <div id="ark-wrap"></div>
                                </TicketLists>
                            </Route>
                            {/* 司机列表页 */}
                            <Route path="/ark/driver-lists" component={DriverList} />
                            {/* 订单列表页 */}
                            <Route path="/ark/order-lists" component={OrderList} />
                            {/* 乘客列表页 */}
                            <Route path="/ark/passenger-lists" component={PassengerList} />
                            <Route path="/editor" component={EditorPage} />
                            <Route path="/ticket/driver" component={window.location.href.includes('source=platform_driver_detail') ? TicketDriverDetail : DriverPage} />
                            <Route path="/ticket/passenger"  component={window.location.href.includes('source=platform_passenger_detail') ? TicketPassengerDetail : PassengerPage} />
                            {/* 车头详情页 */}
                            <Route path="/ticket/fleet" component={window.location.href.includes('source=platform_driver_detail') ? TicketDriverDetail : FleetPage} />
                            {/* 问题分类管理页 */}
                            <Route path="/ark/question-lists" component={QuestionList} />
                            {/* 问题分类新增页 */}
                            <Route path="/ark/question-create" component={QuestionCreate} />
                            {/* 问题分类管理页 */}
                            <Route path="/ark/question-edit" component={QuestionCreate} />
                            {/* 问题分类详情页 */}
                            <Route path="/ark/question-detail" component={QuestionDetail} />
                            <Route path="/ticket/detail" component={TicketDetail} />
                            <Route component={PageErr} />
                            {/* 占位id #ark=wrap */}
                            <div id="ark-wrap"></div>
                        </Switch>
                    </React.Suspense>
                </HashRouter>
            </ConfigProvider>
        </Provider>
    </IntlProvider>
);

// 主应用的生命周期
const mainLifeCycles = {
    beforeLoad: [
        (app: any) => {
            console.log("[LifeCycle---] before load %c%s", "color: green;", app.name);
            return Promise.resolve();
        }
    ],
    beforeMount: [
        (app: any) => {
            console.log("[LifeCycle---] before mount %c%s", "color: green;", app.name);
            return Promise.resolve();
        }
    ],
    afterMount: [
        (app: any) => {
            console.log("[LifeCycle---] after mount %c%s", "color: green;", app.name);
            return Promise.resolve();
        }
    ],
    afterUnmount: [
        (app: any) => {
            console.log("[LifeCycle---] after unmount %c%s", "color: green;", app.name);
            return Promise.resolve();
        }
    ]
}
const microAppsList: any = []
// 初始化微应用
QIANKUN_CONFIG.map((site: any) => {
    let microAppsParams = {
        name: site.name,
        entry: site.entry,
        container: "#ark-wrap",
        loader: () => { },
        activeRule: `${COMMON_PATH}${site.activeRule}`,
        props: {
            // SuperOmega,
            newNav: true,
            QKfrom: 'ticket',
            ...site.props
        }
    }
    return microAppsList.push(microAppsParams)
})
registerMicroApps(microAppsList, mainLifeCycles);

let delay = 500,
    timeCount = 10

let interval = setInterval(() => {
    if (document.querySelector("#ark-wrap")) {
        clearInterval(interval)
        start({});
    } else {
        // eslint-disable-next-line
        --timeCount
    }
}, delay)

setTimeout(() => {
    start({});
}, delay)
export default App